<template>
  <div :class="$style.container">
    <HgSkeleton v-if="!chartMounted" height="195px" :class="$style.loaderChart" />
    <VueApexCharts
      v-else
      :type="props.chartType"
      :options="chartOptions"
      :series="chartSeries"
      :class="$style.chartClass"
    />
  </div>
</template>
<script setup lang="ts">
import { hgNumber, HgSkeleton, HgSupportedLanguage } from '@smg-real-estate/domus';
import type { ApexOptions } from 'apexcharts';
import cloneDeep from 'lodash.clonedeep';
import maxBy from 'lodash.maxby';
import minBy from 'lodash.minby';
import { storeToRefs } from 'pinia';
import { computed, defineAsyncComponent, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { type PriceEvolutionDataItem } from '@/models/iazi/queries/getPriceEvolutionResponse';
import useMapStore from '@/stores/useMapStore';
import {
  STEPSIZEMINLIMIT,
  STEPSIZEMINVALUE,
  TICKAMOUNTMINLIMIT,
  XAXISMAXTICKS,
} from '@/utils/constants';
import { calculateEvolution } from '@/utils/functions';

const VueApexCharts = defineAsyncComponent(() => import('vue3-apexcharts'));

interface Props {
  chartType: 'area' | 'bar' | undefined;
  plotOptions?: {
    bar: {
      columnWidth: string;
      borderRadius: number;
    };
  };
  fill: {
    type: string;
    opacity?: number;
    colors: Array<string>;
  };
  stroke: {
    width: number;
    colors: Array<string>;
  };
  isIS24: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  chartType: 'area',
  isIS24: true,
  plotOptions: undefined,
});
const emit = defineEmits<{
  /* eslint-disable unused-imports/no-unused-vars */
  (e: 'currentDevelopment', development: number): void;
  (e: 'developmentStartTime', developmentTime: number | string): void;
}>();
const mapStore = useMapStore();
const { data, propertyType, selectedFilterRange } = storeToRefs(mapStore);
const { t, locale } = useI18n();
const selectedRange = ref<number>(0);
const xaxisCounter = ref<number>(0);
const yMinCounter = ref<number>(0);
const yMaxCounter = ref<number>(0);
const chartMounted = ref<boolean>(false);
const currDevelopment = ref<number>(0);
const startTime = ref<number | string>(0);
const evoXAxis = ref<Array<number | string>>([]);
const evoYAxis = ref<number[]>([]);

const xAxisTooltip = ref<Array<number | string>>([]);
/* define computed variables */
const currDataArray = computed<Array<PriceEvolutionDataItem> | undefined>(() =>
  sortResponseArray(data.value.priceEvoData?.results?.[propertyType.value]),
);

const sortResponseArray = (responseArray: Array<PriceEvolutionDataItem> | undefined) => {
  return responseArray?.sort((a, b) => a.timePeriod - b.timePeriod);
};

const numberFormatted = (value: number): string => {
  return hgNumber(value, locale.value as HgSupportedLanguage);
};
const yMinDisplay = computed<number | undefined>(() => {
  return yMinCounter.value;
});
const yMaxDisplay = computed<number | undefined>(() => {
  return yMaxCounter.value;
});
const stepSizeDisplay = computed<number | undefined>(() => {
  return yMaxCounter.value - yMinCounter.value <= STEPSIZEMINLIMIT ? STEPSIZEMINVALUE : undefined;
});
const tickAmountDisplay = computed<number | undefined>(() => {
  if (yMaxCounter.value - yMinCounter.value == 0) {
    return 1;
  }
  return yMaxCounter.value - yMinCounter.value < TICKAMOUNTMINLIMIT
    ? yMaxCounter.value - yMinCounter.value
    : TICKAMOUNTMINLIMIT;
});

const chartOptions = computed((): ApexOptions => {
  return {
    chart: {
      type: props.chartType,
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },
    plotOptions: props.plotOptions ? props.plotOptions : {},
    dataLabels: {
      enabled: false,
    },
    fill: props.fill,
    xaxis: {
      type: 'category',
      overwriteCategories: evoXAxis.value as string[],
      tooltip: {
        enabled: false,
      },
      tickPlacement: 'on',
      crosshairs: {
        width: 1,
        stroke: {
          width: 1,
          dashArray: 0,
        },
        fill: {
          type: 'solid',
          color: '#00627A',
        },
      },
      labels: {
        offsetX: -2,
      },
    },
    markers: {
      hover: {
        size: 0,
      },
    },
    stroke: props.stroke,
    yaxis: {
      labels: {
        formatter: function (val: number) {
          return props.isIS24 ? Math.round(val) + '%' : numberFormatted(Math.round(val));
        },
      },
      min: yMinDisplay.value,
      max: yMaxDisplay.value,
      stepSize: stepSizeDisplay.value,
      tickAmount: tickAmountDisplay.value,
    },
    tooltip: {
      x: {
        formatter: function (value: number) {
          return xAxisTooltip.value[value - 1] as string;
        },
      },
      marker: {
        show: false,
      },
      custom: (arg: any) => {
        const index = arg.dataPointIndex;
        const seriesIndex = arg.seriesIndex;
        const series = arg.series;
        return props.isIS24
          ? `<div class="tooltipPop"><p class="tooltipContentX">${xAxisTooltip.value[index]}</p><p class="tooltipContentY">${series[seriesIndex][index]}%</p></div>`
          : `<div class="tooltipPop"><p class="tooltipContentX">${xAxisTooltip.value[index]}</p><p class="tooltipContentY">${numberFormatted(series[seriesIndex][index])} CHF</p></div>`;
      },
    },
  };
});
const chartSeries = computed(() => {
  return [
    {
      data: evoYAxis.value,
    },
  ];
});

/* functions are defined here */
const filterData = async () => {
  const clipLength = 2 * selectedRange.value + 1;
  const clipDataArray = cloneDeep(currDataArray.value?.slice(-clipLength));
  if (clipDataArray) {
    const factor = clipDataArray[0].factorValue;
    for (const element of clipDataArray) {
      const factorValue = element.factorValue;
      element.evolution = parseFloat(calculateEvolution(factor, factorValue).toFixed(2));
    }
    initData(clipDataArray);
  }
};
const setMaxMin = (responseArray: Array<PriceEvolutionDataItem>) => {
  if (props.isIS24) {
    yMaxCounter.value = Math.ceil(maxBy(responseArray, 'evolution')?.evolution ?? 0);
    yMinCounter.value = Math.floor(minBy(responseArray, 'evolution')?.evolution ?? 0);
    if (
      yMinCounter.value != 0 &&
      yMaxCounter.value != 0 &&
      yMaxCounter.value % yMinCounter.value != 0
    ) {
      yMaxCounter.value = yMaxCounter.value + (yMaxCounter.value % yMinCounter.value);
    }
  } else {
    yMaxCounter.value =
      Math.ceil((maxBy(responseArray, 'priceValue')?.priceValue ?? 0) / 100) * 100;
    yMinCounter.value =
      Math.floor((minBy(responseArray, 'priceValue')?.priceValue ?? 0) / 100) * 100 - 100;
  }
};

const evolutionYaxis = (element: PriceEvolutionDataItem): number => {
  if (props.isIS24) {
    return element.evolution;
  } else {
    return element.priceValue;
  }
};
const roundValues = (
  responseArray: Array<PriceEvolutionDataItem>,
): Array<PriceEvolutionDataItem> => {
  if (props.isIS24) {
    return responseArray;
  } else {
    let roundedArray: Array<PriceEvolutionDataItem> = [];
    for (const priceEvoItem of responseArray) {
      let priceValue = Math.round(priceEvoItem.priceValue / 100);
      priceEvoItem.priceValue = priceValue * 100;
      roundedArray.push(priceEvoItem);
    }
    return roundedArray;
  }
};
const initData = (responseArray: Array<PriceEvolutionDataItem> | null | undefined) => {
  startTime.value = 0;
  currDevelopment.value = 0;
  xaxisCounter.value = 0;
  if (responseArray && responseArray.length > 1) {
    currDevelopment.value = responseArray[responseArray.length - 1].evolution;
    const dataArray = roundValues(responseArray);
    setMaxMin(dataArray);
    const tempEvoX: (number | string)[] = [];
    const tempEvoY: number[] = [];
    const tempTooltipX: (number | string)[] = [];
    for (let element of dataArray) {
      tempEvoY.push(evolutionYaxis(element));
      // eslint-disable-next-line max-depth
      if (element.timePeriod % 100 == 12) {
        tempTooltipX.push(Math.round(element.timePeriod / 100) + 1);
        tempEvoX.push(Math.round(element.timePeriod / 100) + 1);
      } else {
        tempTooltipX.push(
          t('priceTrends.midYear', [Math.round(element.timePeriod / 100)]).toString(),
        );
        tempEvoX.push('');
      }
      xaxisCounter.value++;
    }
    startTime.value = tempTooltipX[0];
    initFinal(tempEvoX, tempEvoY, tempTooltipX);
  }
};
const initFinal = (
  tempEvoX: (number | string)[],
  tempEvoY: number[],
  tempTooltipX: (number | string)[],
) => {
  emit('currentDevelopment', currDevelopment.value);
  emit('developmentStartTime', startTime.value);
  evoXAxis.value = [];
  evoYAxis.value = [];
  xAxisTooltip.value = [];
  if (xaxisCounter.value > XAXISMAXTICKS) {
    evoXAxis.value = tempEvoX.slice(-XAXISMAXTICKS);
    evoYAxis.value = tempEvoY.slice(-XAXISMAXTICKS);
    xAxisTooltip.value = tempTooltipX.slice(-XAXISMAXTICKS);
  } else {
    evoXAxis.value = tempEvoX;
    evoYAxis.value = tempEvoY;
    xAxisTooltip.value = tempTooltipX;
  }
};

onMounted(async () => {
  chartMounted.value = true;
});

watch(
  currDataArray,
  () => {
    initData(currDataArray.value);
  },
  { immediate: true, deep: true },
);

watch(locale, () => {
  initData(currDataArray.value);
});
watch(selectedFilterRange, () => {
  if (chartMounted.value && selectedFilterRange.value != 0) {
    selectedRange.value = selectedFilterRange.value;
    filterData();
  }
});
</script>

<style lang="scss" module>
@use '@/styles/variables' as *;

$chart-height: 150px;

.container {
  width: 100%;
  min-height: $chart-height;

  .skeleton {
    height: calc($chart-height - $space-100);
    margin-top: $space-100;
    border-radius: $border-radius;
  }
}

.chartClass {
  width: 100%;
  min-height: $chart-height;

  [class*='apexcharts-xaxis'] {
    font-family: unset !important;

    @include caption;
  }

  [class*='apexcharts-yaxis'] {
    font-family: unset !important;

    @include caption;
  }

  [class*='apexcharts-tooltip'] {
    top: 20px !important;
    background: rgb(255 255 255 / 80%) !important;
    border: none !important;
    border-radius: 0;
    box-shadow: none !important;
  }

  [class*='apexcharts-xcrosshairs'] {
    height: fit-content !important;
  }

  [class*='tooltipPop'] {
    display: inline-flex;
    flex-direction: column;
    align-items: flex-start;
    width: $space-500;
    padding: $space-25;
  }

  [class*='tooltipContentX'] {
    margin-top: 0;
    margin-bottom: 0;

    @include body-3;
  }

  [class*='tooltipContentY'] {
    margin-top: 0;
    margin-bottom: 0;

    @include body-3-bold;
  }
}
</style>
